import { Pressable, StyleSheet, Text, View } from "react-native";
import type { Palette } from "@heraion/shared";
import { CreateNavButton } from "./CreateNavButton";
import { stateAria } from "@heraion/ui";

// The desktop console nav rail (>= WIDE_BREAKPOINT). Pure navigation now: the
// brand + account menu moved up to the TopBar, and the role switch into the
// account menu, so the rail carries the surface tabs (RW-52: plus the Create
// affordance ABOVE them, see below). On a phone the same nav lives in the
// bottom tab bar; on a wide viewport it becomes this left rail — the
// canonical responsive translation.
//
// Order mirrors mobile (Plan · Workouts · Progress). Settings is NOT a rail row
// anymore (it lives in the account menu); the shell filters it out of `items`.
export type SidebarItem = { key: string; label: string };

export function Sidebar({
  p,
  items,
  activeKey,
  onSelect,
  onCreatePress,
  createLabel,
}: {
  p: Palette;
  items: SidebarItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  /** RW-52.1 — undefined → no affordance at all (admin has none, matching
   *  mobile). No longer a "mount after this item" position: the button now
   *  sits in its own zone above the whole list (see `createWrap` below), so
   *  there is nothing left to anchor it to a particular row. */
  onCreatePress?: () => void;
  createLabel?: string;
}) {
  const styles = makeStyles(p);
  return (
    <View style={styles.rail}>
      {/* RW-52.1 (Antenore) — the Android FAB is a circle raised over a
          horizontal bar; that gesture read as "an affordance apart from the
          destinations", not as "sits at this particular spot". Translating
          it into a vertical list as a circle wedged between two rows lost
          the gesture and kept only the old position. Here it becomes its own
          zone instead: a full-width primary button for what you CREATE,
          separated by a rule from the list of where you GO — two different
          jobs get two different zones, top to bottom. */}
      {onCreatePress && createLabel ? (
        <View style={styles.createWrap}>
          <CreateNavButton
            p={p}
            variant="full"
            onPress={onCreatePress}
            accessibilityLabel={createLabel}
            label={createLabel}
          />
        </View>
      ) : null}
      <View style={styles.nav} accessibilityRole="tablist">
        {items.map((it) => {
          const on = it.key === activeKey;
          return (
            <Pressable
              key={it.key}
              style={[styles.item, on && styles.itemOn]}
              onPress={() => onSelect(it.key)}
              accessibilityRole="tab"
              accessibilityState={{ selected: on }}
              {...stateAria({ selected: on }, "tab")}
              accessibilityLabel={it.label}
            >
              {/* Active cue is shape (left accent bar) + weight + colour, so
                  it never reads by colour alone (reduced-vision a11y). */}
              <View style={[styles.accent, on && styles.accentOn]} />
              <Text style={[styles.itemLabel, on && styles.itemLabelOn]}>
                {it.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const RAIL_WIDTH = 210;

function makeStyles(p: Palette) {
  return StyleSheet.create({
    rail: {
      width: RAIL_WIDTH,
      flexShrink: 0,
      height: "100%",
      backgroundColor: p.surface,
      borderRightWidth: 1,
      borderRightColor: p.border,
      paddingHorizontal: 12,
      paddingTop: 14,
      paddingBottom: 14,
    },
    nav: { gap: 2 },
    item: {
      flexDirection: "row",
      alignItems: "center",
      minHeight: 44,
      borderRadius: 10,
      paddingRight: 12,
    },
    itemOn: { backgroundColor: p.surface2 },
    accent: {
      width: 3,
      height: 20,
      borderRadius: 2,
      marginRight: 11,
      marginLeft: 4,
      backgroundColor: "transparent",
    },
    accentOn: { backgroundColor: p.brand500 },
    itemLabel: { color: p.ink3, fontSize: 15, fontWeight: "600" },
    itemLabelOn: { color: p.ink, fontWeight: "700" },
    // RW-52.1 — its own zone above the nav list, closed off by a rule so the
    // "create" job and the "go to" job below it never blur into one strip.
    createWrap: {
      marginBottom: 12,
      paddingBottom: 12,
      borderBottomWidth: 1,
      borderBottomColor: p.border,
    },
  });
}
